import { useEffect, useState } from 'react';
import { oauthProviders, oauthStartUrl, type OAuthProvider } from '../lib/rpc-client';

function GitHubMark() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 009 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 013.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 000 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 009 0 9 9 0 00.96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

const PROVIDERS: { id: OAuthProvider; label: string; Icon: () => JSX.Element }[] = [
  { id: 'github', label: 'GitHub', Icon: GitHubMark },
  { id: 'google', label: 'Google', Icon: GoogleMark },
];

// One lookup per page load, shared by sign-in and sign-up.
let configured: Promise<OAuthProvider[]> | null = null;

function useConfiguredProviders(): OAuthProvider[] {
  const [ids, setIds] = useState<OAuthProvider[]>([]);
  useEffect(() => {
    let live = true;
    configured ??= oauthProviders().catch(() => {
      configured = null;
      return [];
    });
    configured.then((x) => live && setIds(x));
    return () => {
      live = false;
    };
  }, []);
  return ids;
}

/** GitHub and Google sign-in, only for the providers saas reports configured.
 *  Plain links: the start URL is a full-page redirect through saas to the
 *  provider and back to /account. */
export function OAuthButtons({ verb = 'Continue' }: { verb?: string }) {
  const ids = useConfiguredProviders();
  const shown = PROVIDERS.filter((p) => ids.includes(p.id));
  if (shown.length === 0) return null;
  return (
    <div className="oauth-buttons">
      {shown.map(({ id, label, Icon }) => (
        <a key={id} className="btn oauth-btn" href={oauthStartUrl(id)} data-testid={`oauth-${id}`}>
          <Icon />
          {verb} with {label}
        </a>
      ))}
      <div className="oauth-divider" role="separator">
        <span>or with email</span>
      </div>
    </div>
  );
}
